import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const bottomLinks = (isAdmin) => [
  { to: '/',            end: true,  icon: 'home',     label: 'Home'    },
  { to: '/matches',     end: false, icon: 'calendar', label: 'Matches' },
  { to: '/leaderboard', end: false, icon: 'trophy',   label: 'Table'   },
  ...(isAdmin ? [{ to: '/admin', end: false, icon: 'settings', label: 'Admin' }] : []),
  { to: '/account',     end: false, icon: 'user',     label: 'Account' },
];

const topLinks = (isAdmin) => [
  { to: '/',            end: true,  label: 'Home'     },
  { to: '/matches',     end: false, label: 'Matches'  },
  { to: '/leaderboard', end: false, label: 'Table'    },
  { to: '/changelog',   end: false, label: "What's New" },
  ...(isAdmin ? [{ to: '/admin', end: false, label: 'Admin' }] : []),
  { to: '/account',     end: false, label: 'Account'  },
];

const ICONS = {
  home: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10',
  calendar: 'M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M3 10h18 M8 2v4 M16 2v4',
  trophy: 'M6 9H4.5a2.5 2.5 0 0 1 0-5H6 M18 9h1.5a2.5 2.5 0 0 0 0-5H18 M4 22h16 M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22 M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22 M18 2H6v7a6 6 0 0 0 12 0z',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
};

function Icon({ name, className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[name].split(' M').map((d, i) => <path key={i} d={(i ? 'M' : '') + d} />)}
    </svg>
  );
}

function BrandMark() {
  return (
    <svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <polygon points="12,7 16,10.2 14.5,15 9.5,15 8,10.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

export default function Nav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.groups?.includes('admin') || user?.groups?.includes('league-admin');
  const onAccount = location.pathname === '/account';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <nav className="nav">
        <span className="nav__brand"><BrandMark />WC<span className="yr">26</span></span>

        <div className="nav__links">
          {topLinks(isAdmin).map(({ to, end, label }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) => `nav__link${isActive ? ' active' : ''}`}>
              {to === '/account' ? (user?.name || label) : label}
            </NavLink>
          ))}
          <button className="btn btn-ghost btn-sm" onClick={handleLogout}>Out</button>
        </div>

        <button className="nav__mobile-logout" onClick={handleLogout}>Sign out</button>
      </nav>

      <nav className="bottom-nav" aria-label="Main navigation">
        {bottomLinks(isAdmin).map(({ to, end, icon, label }) => (
          <NavLink key={to} to={to} end={end}
            className={({ isActive }) => `bottom-nav__item${isActive ? ' active' : ''}`}>
            <Icon name={icon} className="bottom-nav__icon" />
            {label}
          </NavLink>
        ))}
      </nav>
    </>
  );
}
